import { Injectable } from '@angular/core';
import { notificationError } from '@components/layout/error/error.utils';
import { Period } from '@entity/filter-data.entity';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { RateService } from '@service/rate.service';
import { ThresholdService } from '@service/threshold.service';
import {
  changeDashboardFilters,
  changeDashboardFiltersSuccess,
  changeHistogramGranularity,
  initAllCharts,
  retrieveDeliverabilityIndicators,
  retrieveDeliverabilityIndicatorsFailure,
  retrieveDeliverabilityIndicatorsSuccess,
  retrieveHistogramData,
  retrieveHistogramDataFailure,
  retrieveHistogramDataSuccess,
  retrieveProvidersStatsData,
  retrieveProvidersStatsDataFailure,
  retrieveProvidersStatsDataSuccess,
  retrieveThresholdData,
  retrieveThresholdDataFailure,
  retrieveThresholdDataSuccess
} from '@store/dashboard/dashboard.action';
import { loadEmailProductsSuccess } from '@store/products/products.action';
import { getEmailEventTypesByProductIdMap } from '@store/products/products.selector';
import { State } from '@store/state';
import { setTimeToEndOfDay, setTimeToStartOfDay } from '@utils/date.utils';
import { catchError, forkJoin, map, of, switchMap, withLatestFrom } from 'rxjs';
import { getDashboardFilters, getHistogramMailSentData } from './dashboard.selector';

@Injectable()
export class DashboardEffect {
  constructor(
    public store: Store<State>,
    public actions$: Actions,
    public rateService: RateService,
    public thresholdService: ThresholdService
  ) { }

  retrieveThresholdDataOnLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadEmailProductsSuccess),
      map(({ emailEventTypesByProductId }) =>
        retrieveThresholdData({ productIds: Object.keys(emailEventTypesByProductId) })
      )
    )
  );

  updateProductId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changeDashboardFilters),
      withLatestFrom(this.store.select(getEmailEventTypesByProductIdMap)),
      map(([{ filters }, emailEventTypesByProductId]) => {
        if (filters.productId.length === 0) {
          return changeDashboardFiltersSuccess({
            filters: { ...filters, eventType: [] },
          });
        }
        const eventTypeData = [
          ...new Set(filters.productId.flatMap((productId) => emailEventTypesByProductId[productId] ?? []))
        ].sort((a, b) => a.localeCompare(b));
        return changeDashboardFiltersSuccess({
          filters: {
            ...filters,
            eventType: filters.eventType.filter((et) => eventTypeData.includes(et)),
          },
        });
      })
    )
  );

  // Update chart when filter change
  updateCharts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initAllCharts, changeDashboardFiltersSuccess),
      withLatestFrom(this.store.select(getHistogramMailSentData)),
      switchMap(([{ filters }, { currentGranularity }]) =>
        [
          // all charts need to be here
          retrieveHistogramData({ filters, granularity: currentGranularity }),
          retrieveDeliverabilityIndicators({ filters }),
          retrieveProvidersStatsData({ filters }),
        ]
      )
    )
  );
  // Custom effect to update charts when granularity changes
  updateChartsOnGranularityChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changeHistogramGranularity),
      withLatestFrom(this.store.select(getDashboardFilters)),
      map(([{ granularity }, filters]) => retrieveHistogramData({ filters, granularity })
      )
    )
  );


  //////////// Graphicals data retrieval ////////////


  retrieveHistogramData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(retrieveHistogramData),
      switchMap(({ filters, granularity }) =>
        this.rateService.getRateEmailStats(filters, granularity).pipe(
          map((data) => retrieveHistogramDataSuccess({
            histogramData: {
              currentGranularity: granularity,
              data
            }
          })),
          catchError((error) =>
            of(retrieveHistogramDataFailure({ error: notificationError(error) }))
          )
        )
      )
    )
  );

  retrieveDeliverabilityIndicatorsData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(retrieveDeliverabilityIndicators),
      switchMap(({ filters }) =>
        forkJoin({
          allOnPeriod: this.rateService.getRateEmailStatsWhenSent(filters),
          yesterday: this.rateService.getRateEmailStatsWhenSent({
            ...filters,
            period: getPeriodFromYesterday()
          }),
          lastSevenFullDays: this.rateService.getRateEmailStatsWhenSent({
            ...filters,
            period: getPeriodFromLastSevenFullDays()
          }),
        }).pipe(
          map(({ allOnPeriod, yesterday, lastSevenFullDays }) => retrieveDeliverabilityIndicatorsSuccess({
            allOnPeriod,
            yesterday,
            lastSevenFullDays
          })),
          catchError((error) =>
            of(retrieveDeliverabilityIndicatorsFailure({ error: notificationError(error) }))
          )
        )
      )
    )
  );

  retrieveThresholdData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(retrieveThresholdData),
      switchMap(({ productIds }) =>
        this.thresholdService.getThresholds(productIds).pipe(
          map((data) => retrieveThresholdDataSuccess({
            thresholdData: data
          })),
          catchError((error) =>
            of(retrieveThresholdDataFailure({ error: notificationError(error) }))
          )
        )
      )
    )
  );

  retrieveProvidersStatsData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(retrieveProvidersStatsData),
      switchMap(({ filters }) =>
        forkJoin({
          allOnPeriod: this.rateService.getRateProviderStats(filters),
          yesterday: this.rateService.getRateProviderStats({
            ...filters,
            period: getPeriodFromYesterday()
          }),
        }).pipe(
          map(({ allOnPeriod, yesterday }) => retrieveProvidersStatsDataSuccess({
            allOnPeriod,
            yesterday
          })),
          catchError((error) =>
            of(retrieveProvidersStatsDataFailure({ error: notificationError(error) }))
          )
        )
      )
    )
  );
}

function getPeriodFromYesterday(): Period {
  const now = new Date();
  now.setDate(now.getDate() - 1);
  return {
    from: setTimeToStartOfDay(now).getTime(),
    to: setTimeToEndOfDay(now).getTime(),
  };
}

function getPeriodFromLastSevenFullDays(): Period {

  const dateFrom = new Date();
  dateFrom.setDate(dateFrom.getDate() - 8);

  const dateTo = new Date()
  dateTo.setDate(dateTo.getDate() - 1);

  return {
    from: setTimeToStartOfDay(dateFrom).getTime(),
    to: setTimeToEndOfDay(dateTo).getTime(),
  };}

